import { memo, type MouseEvent } from "react";
import Markdown from "react-markdown";
import "./terms.style.css";

interface TermsMarkdownProps {
  content: string;
  /**
   * When true, renders compactly for inline use next to a checkbox (smaller
   * margins). Use the default (false) for the full detail body.
   */
  inline?: boolean;
  className?: string;
}

/** Minimal structural view of a hast node (what react-markdown hands to plugins). */
interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

const hastText = (node: HastNode): string =>
  node.type === "text"
    ? (node.value ?? "")
    : (node.children ?? []).map(hastText).join("");

/**
 * GitHub-style heading slug: lowercase, drop punctuation, spaces → `-`.
 * `# 1. SEND User Agreement` → `1-send-user-agreement`, so a markdown index
 * written for GitHub (`[…](#1-send-user-agreement)`) resolves here too.
 */
const slugify = (text: string): string =>
  text
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\p{M}\s-]/gu, "")
    .replace(/\s/g, "-");

/**
 * Tiny rehype plugin that gives every heading an `id` (deduplicated with
 * `-1`, `-2`, … like GitHub). Inlined instead of pulling `rehype-slug` so the
 * widget stays dependency-light.
 */
const rehypeHeadingIds = () => (tree: HastNode) => {
  const seen = new Map<string, number>();
  const walk = (node: HastNode) => {
    if (node.type === "element" && /^h[1-6]$/.test(node.tagName ?? "")) {
      const base = slugify(hastText(node));
      if (base) {
        const count = seen.get(base) ?? 0;
        seen.set(base, count + 1);
        node.properties = {
          ...node.properties,
          id: count === 0 ? base : `${base}-${count}`,
        };
      }
    }
    node.children?.forEach(walk);
  };
  walk(tree);
};

const isAnchorHref = (href?: string): href is string =>
  typeof href === "string" && href.startsWith("#");

/**
 * In-document anchor (e.g. a table of contents `[Section](#section)`): scroll
 * to the heading inside the widget's scroll container instead of letting the
 * browser touch the URL hash (which the SPA router would otherwise own).
 */
const scrollToAnchor = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
  const target = document.getElementById(decodeURIComponent(href.slice(1)));
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ behavior: "smooth", block: "start" });
};

/**
 * Renders partner-configured Terms & Conditions markdown. External links open
 * in a new tab so they never break the widget's single-page navigation;
 * `#anchor` links scroll within the document so a markdown index works.
 *
 * `inline` uses a `<span>` wrapper (and maps markdown `<p>` to `<span>`) so
 * the intro can sit inside a checkbox `<label>` without invalid `p > div`.
 */
export const TermsMarkdown = memo(
  ({ content, inline = false, className }: TermsMarkdownProps) => {
    const cls = `terms-markdown${inline ? " terms-markdown--inline" : ""}${
      className ? ` ${className}` : ""
    }`;
    const markdown = (
      <Markdown
        rehypePlugins={[rehypeHeadingIds]}
        components={{
          a: ({ href, children }) =>
            isAnchorHref(href) ? (
              <a href={href} onClick={(e) => scrollToAnchor(e, href)}>
                {children}
              </a>
            ) : (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            ),
          ...(inline ? { p: ({ children }) => <span>{children}</span> } : {}),
        }}
      >
        {content}
      </Markdown>
    );

    if (inline) {
      return <span className={cls}>{markdown}</span>;
    }
    return <div className={cls}>{markdown}</div>;
  }
);
